
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const EBITDACalculator = () => {
  const [revenue, setRevenue] = useState<string>('1000000');
  const [operatingExpenses, setOperatingExpenses] = useState<string>('600000');
  const [depreciation, setDepreciation] = useState<string>('50000');
  const [amortization, setAmortization] = useState<string>('25000');
  const [result, setResult] = useState<{
    ebitda: number;
    ebitdaMargin: number;
    ebit: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateEBITDA = () => {
    try {
      setError(null);
      
      const rev = parseFloat(revenue);
      const opEx = parseFloat(operatingExpenses);
      const dep = parseFloat(depreciation);
      const amort = parseFloat(amortization);

      if (isNaN(rev) || isNaN(opEx) || isNaN(dep) || isNaN(amort)) {
        setError("Please enter valid numbers");
        return;
      }

      if (rev <= 0) {
        setError("Revenue must be positive");
        return;
      }

      if (opEx < 0 || dep < 0 || amort < 0) {
        setError("Expenses cannot be negative");
        return;
      }

      // EBITDA = Revenue - Operating Expenses (excluding D&A)
      const ebitda = rev - opEx;
      
      // EBIT = EBITDA - Depreciation - Amortization
      const ebit = ebitda - dep - amort;
      
      // EBITDA Margin = (EBITDA / Revenue) × 100
      const ebitdaMargin = (ebitda / rev) * 100;

      setResult({
        ebitda,
        ebitdaMargin,
        ebit
      });

    } catch (err) {
      setError("Error calculating EBITDA");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">EBITDA Calculator</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="revenue">Total Revenue ($)</Label>
              <Input
                id="revenue"
                type="number"
                value={revenue}
                onChange={(e) => setRevenue(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="operating-expenses">Operating Expenses ($)</Label>
              <Input
                id="operating-expenses"
                type="number"
                value={operatingExpenses}
                onChange={(e) => setOperatingExpenses(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Excluding depreciation and amortization
              </p>
            </div>

            <div>
              <Label htmlFor="depreciation">Depreciation ($)</Label>
              <Input
                id="depreciation"
                type="number"
                value={depreciation}
                onChange={(e) => setDepreciation(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="amortization">Amortization ($)</Label>
              <Input
                id="amortization"
                type="number"
                value={amortization}
                onChange={(e) => setAmortization(e.target.value)}
                className="mt-1"
              />
            </div>

            <Button onClick={calculateEBITDA} className="w-full">
              Calculate EBITDA
            </Button>

            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {result !== null && (
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                <h3 className="font-medium">Results</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">EBITDA</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(result.ebitda)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">EBITDA Margin</p>
                    <p className="text-xl font-bold text-blue-600">
                      {result.ebitdaMargin.toFixed(1)}%
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">EBIT</p>
                    <p className="text-xl font-bold text-purple-600">
                      {formatCurrency(result.ebit)}
                    </p>
                  </div>
                </div>

                <div className="mt-4 text-sm">
                  <p><strong>About EBITDA:</strong></p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li><strong>EBITDA:</strong> Earnings Before Interest, Taxes, Depreciation, and Amortization</li>
                    <li><strong>EBIT:</strong> Earnings Before Interest and Taxes</li>
                    <li>Higher EBITDA margin indicates better operational efficiency</li>
                    <li>EBITDA is commonly used for company valuation and comparison</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EBITDACalculator;
